import {
  RequirementTableCallback,
  RequirementTableItemData,
  RequirementTableRowConfig
} from "../../types";
import { useMemo } from "react";
import { useWSQueries } from "@ws-react-query";
import { RequirementType } from "@wingspanhq/payments/dist/interfaces";
import { usePayerIdentityVerificationConfig } from "./configs/payer/usePayerIdentityVerificationConfig";
import { usePayeeIdentityVerificationConfig } from "./configs/payee/usePayeeIdentityVerificationConfig";
import {
  buildConfigFields,
  sharedRequirementsQueriesConfig
} from "../../utils";
import { getIdentityVerificationRequestFromCache } from "./configs/helpers/getIdentityVerificationRequestFromCache";
import { getIdentityVerificationRequest } from "../../../../services/identityVerification/getIdentityVerificationRequest";
import { QUERY_IDENTITY_VERIFICATION_REQUEST } from "../../../../query/identityVerification/keys";
import { IIdentityVerificationRequestResponse } from "@wingspanhq/users";
import { QUERY_PAYER } from "../../../../query/payers/keys";
import { getPayer } from "../../../../services/payers";
import { useUserId } from "../../../../query/hooks/helpers";

const useContextQueries = (requirements: RequirementTableItemData[]) => {
  const userId = useUserId();

  const identityVerificationsRequestQueries = requirements
    .filter(
      requirement =>
        requirement.requirementType === RequirementType.IdentityVerification &&
        requirement.dataSourceId
    )
    .map(requirement => ({
      queryKey: [QUERY_IDENTITY_VERIFICATION_REQUEST, requirement.dataSourceId],
      queryFn: () => getIdentityVerificationRequest(requirement.dataSourceId),
      ...sharedRequirementsQueriesConfig
    }));

  const payersQueries = requirements
    .filter(
      requirement =>
        requirement.requirementType === RequirementType.IdentityVerification &&
        requirement.payerId &&
        userId !== requirement.payerId
    )
    .map(requirement => ({
      queryKey: [QUERY_PAYER, requirement.payerId],
      queryFn: () => getPayer(requirement.payerId),
      ...sharedRequirementsQueriesConfig
    }));

  const queries = [...identityVerificationsRequestQueries, ...payersQueries];

  return useWSQueries({ queries });
};

export const useIdentityVerificationRequirementConfig = (
  callback: RequirementTableCallback,
  requirements: RequirementTableItemData[]
): RequirementTableRowConfig[RequirementType.IdentityVerification] => {
  const contextQueries = useContextQueries(requirements);
  const payerConfig = usePayerIdentityVerificationConfig(callback);
  const payeeConfig = usePayeeIdentityVerificationConfig(callback);

  const queriesHash = contextQueries
    .map(query => query.dataUpdatedAt)
    .join(",");

  const fields = useMemo(
    buildConfigFields<
      IIdentityVerificationRequestResponse,
      RequirementType.IdentityVerification
    >({
      payerConfig,
      payeeConfig,
      getAdditionalData: getIdentityVerificationRequestFromCache
    }),
    [queriesHash]
  );

  return {
    ...fields,
    getAdditionalData(requirement, context) {
      return getIdentityVerificationRequestFromCache(requirement, context);
    },
    isLoading: contextQueries.some(query => query.isLoading),
    dataUpdatedAt: queriesHash
  };
};
